class MobileTabMenu extends HTMLElement {
  connectedCallback() {
    if (this._controller) return;
    this._controller = new AbortController();
    const signal = this._controller.signal;
    this.trigger = this.querySelector('[data-menu-open]');
    this.panel = this.querySelector('[data-menu-panel]');
    this.closeButton = this.querySelector('[data-menu-close]');
    this.scrollArea = this.querySelector('[data-menu-scroll]');
    this.tabs = [...this.querySelectorAll('[data-menu-tab]')];
    this.tabPanels = [...this.querySelectorAll('[data-menu-tab-panel]')];
    this.groupButtons = [...this.querySelectorAll('[data-menu-group-toggle]')];
    this.policyButton = this.querySelector('[data-menu-policy-toggle]');
    this.policies = this.querySelector('[data-menu-policies]');

    this.trigger.addEventListener('click', () => this.openMenu(), { signal });
    this.closeButton.addEventListener('click', () => this.closeMenu(), { signal });
    this.tabs.forEach((tab, index) => {
      tab.addEventListener('click', () => this.activateTab(index), { signal });
      tab.addEventListener('keydown', (event) => {
        let next = null;
        if (event.key === 'ArrowLeft') next = (index + 1) % this.tabs.length;
        if (event.key === 'ArrowRight') next = (index - 1 + this.tabs.length) % this.tabs.length;
        if (event.key === 'Home') next = 0;
        if (event.key === 'End') next = this.tabs.length - 1;
        if (next === null) return;
        event.preventDefault();
        this.activateTab(next);
        this.tabs[next].focus();
      }, { signal });
    });
    this.groupButtons.forEach((button) => {
      button.addEventListener('click', () => this.toggleGroup(button), { signal });
    });
    this.policyButton.addEventListener('click', () => {
      const isOpen = this.policyButton.getAttribute('aria-expanded') === 'true';
      this.policyButton.setAttribute('aria-expanded', String(!isOpen));
      this.policies.hidden = isOpen;
    }, { signal });
    this.panel.addEventListener('keydown', (event) => this.onPanelKeydown(event), { signal });
    document.addEventListener('focusin', (event) => {
      if (!this.panel.hidden && !this.panel.contains(event.target)) this.closeButton.focus();
    }, { signal });
  }

  disconnectedCallback() {
    this._controller?.abort();
    this._controller = null;
    document.body.classList.remove('mobile-tab-menu-open');
  }

  openMenu() {
    this.panel.hidden = false;
    this.trigger.setAttribute('aria-expanded', 'true');
    document.body.classList.add('mobile-tab-menu-open');
    this.closeButton.focus();
  }

  closeMenu() {
    this.panel.hidden = true;
    this.trigger.setAttribute('aria-expanded', 'false');
    document.body.classList.remove('mobile-tab-menu-open');
    this.trigger.focus();
  }

  activateTab(index) {
    this.tabs.forEach((tab, i) => {
      const active = i === index;
      tab.setAttribute('aria-selected', String(active));
      tab.tabIndex = active ? 0 : -1;
      this.tabPanels[i].hidden = !active;
    });
    this.groupButtons.forEach((button) => this.setGroup(button, false));
    this.scrollArea.scrollTop = 0;
  }

  setGroup(button, open) {
    button.setAttribute('aria-expanded', String(open));
    const content = document.getElementById(button.getAttribute('aria-controls'));
    if (content) content.hidden = !open;
  }

  toggleGroup(button) {
    const open = button.getAttribute('aria-expanded') !== 'true';
    this.groupButtons.forEach((other) => this.setGroup(other, false));
    this.setGroup(button, open);
  }

  onPanelKeydown(event) {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.closeMenu();
      return;
    }
    if (event.key !== 'Tab') return;
    const focusable = [...this.panel.querySelectorAll('a[href], button:not([disabled])')]
      .filter((element) => element.getClientRects().length > 0 && element.tabIndex >= 0);
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }
}
if (!customElements.get('mobile-tab-menu')) customElements.define('mobile-tab-menu', MobileTabMenu);
